import React, { useEffect, useRef, useState } from "react";
import { Box, Card, Typography, CardContent, Grid, Backdrop } from "@material-ui/core";
import moment from "moment";
import "moment-timezone";
import TabHeader from "src/components/TabHeader/TabHeader";
import { useStyles } from "./style";
import CommonMaterialGrid from "src/components/CommonXGrid/CommonMaterialGrid";
import CommonTextFieldDate from "src/components/CommonTextFieldForDate/CommonTextFieldDate";
import { useFormik } from "formik";
import { object, string } from "yup";
import { GridRowModel } from "@material-ui/x-grid";
import CommonButton from "src/components/CommonButton/CommonButton";
import { fetchWrapper } from "src/api-client/fetchWrapper";
import CommonLoader from "src/components/Loader/CommonLoader";
import CommonAutoCompleteField from "src/components/CommonAutoCompleteField/CommonAutoCompleteField";
import { ContainerInOutReportType } from "src/common/utils/app.dropdowns";
import { addNotificationAction } from "src/app-redux/settings/settingsSlice";
import { useDispatch } from "react-redux";

interface Props {}

const REPORT_API_MAP: Record<string, string> = {
  "Received Containers": "get-received-containers-report",
  "Stock Position": "get-stock-position-report",
  "Delivered Containers": "get-delivered-containers-report",
  "Empty Container": "get-empty-containers-report",
};

const COLUMN_LABELS: Record<string, string> = {
  sNo: "S No",
  containerNo: "Container No",
  sizeType: "Size Type",
  truckNo: "Truck No",
  virNo: "VIR No",
  billNumber: "Bill Number",
  subLine: "Sub Line",
  lpBkNo: "LP/BK No",
  stackingArea: "Stacking Area",
  stackingPos: "Stacking Pos",
  yardShed: "Yard/Shed",
  ageingForTerminal: "Ageing For Terminal",
  eirRemarks: "EIR Remarks",
  loadOffloadType: "Load/Offload Type",
  date: "Date",
  time: "Time",
  line: "Line",
  status: "Status",
  condition: "Condition",
  location: "Location",
  remarks: "Remarks",
  kind: "Kind",
};

const formatHeader = (key: string) =>
  COLUMN_LABELS[key] ||
  key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();

const formatCellValue = (value: any) => {
  if (value === null || value === undefined || value === "") return " ";
  return ` ${value}`;
};

const orderColumnKeys = (keys: string[]) => {
  const afterTruckKeys = ["virNo", "billNumber"];
  const remaining = keys.filter((key) => !afterTruckKeys.includes(key));
  const truckIndex = remaining.indexOf("truckNo");

  if (truckIndex === -1) return keys;

  const presentAfterTruck = afterTruckKeys.filter((key) => keys.includes(key));
  return [
    ...remaining.slice(0, truckIndex + 1),
    ...presentAfterTruck,
    ...remaining.slice(truckIndex + 1),
  ];
};

const buildColumnsFromData = (data: any[]) => {
  if (!data?.length) return [];
  const keys = orderColumnKeys(
    Object.keys(data[0]).filter(
      (key) => key !== "id" && key !== "sNo" && key !== "emptyContainerNo"
    )
  );
  return keys.map((key) => ({
    accessorKey: key,
    header: formatHeader(key),
    type: "string",
    size: 150,
    accessorFn: (row: any) => formatCellValue(row[key]),
  }));
};

const extractReportData = (results: any) => {
  const containers = results?.value?.containers ?? results?.containers ?? [];
  return Array.isArray(containers) ? containers : [];
};

export function ContainerInOutReports({}: Props): React.ReactElement {
  const classes = useStyles();
  const dispatch = useDispatch();
  const moduleName = "Container-In-Out-Reports";
  const pageHeading = "Container-In-Out-Reports";
  const [displayColumns, setDisplayColumns] = React.useState<any>([]);
  const [gridRecord, setGridRecord] = React.useState<GridRowModel[]>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const datePickerRef: any = useRef(null);
  const isExportString = localStorage.getItem("isAllowToExportData");
  const isExport = isExportString === "true";

  const validationSchema = object({
    reportDate: string().required("This field is required."),
    type: string().required("This field is required."),
  });

  const fetchReport = async (reportDate: string, type: string) => {
    const endpoint = REPORT_API_MAP[type];
    if (!endpoint) return;

    const url = `${endpoint}?reportDate=${encodeURIComponent(reportDate || "")}`;

    setGridRecord([]);
    setDisplayColumns([]);
    setLoader(true);
    try {
      const results = await fetchWrapper.get(url);
      const reportData = extractReportData(results).map((row: any, index: number) => ({
        ...row,
        id: row.sNo ?? index + 1,
      }));
      setGridRecord(reportData);
      setDisplayColumns(buildColumnsFromData(reportData));
    } catch (error: any) {
      dispatch(
        addNotificationAction({
          message: error.message || "Error fetching report data",
          options: { variant: "error" },
        })
      );
    }
    setLoader(false);
  };

  const formik = useFormik({
    initialValues: {
      reportDate: moment().startOf("day").format("YYYY-MM-DDTHH:mm"),
      type: "Received Containers",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      await fetchReport(values.reportDate, values.type);
    },
  });

  useEffect(() => {
    fetchReport(moment().startOf("day").format("YYYY-MM-DDTHH:mm"), "Received Containers");
  }, []);

  const exportHandler = async (rows: any[]) => {
    dispatch(
      addNotificationAction({
        message: "Your download will begin shortly",
        options: { variant: "success" },
      })
    );

    return rows.map((data: any) => {
      const record: Record<string, any> = {};
      displayColumns.forEach((col: any) => {
        const key = col.accessorKey;
        record[col.header] = data[key] ?? "";
      });
      return record;
    });
  };

  const resetSearch = () => {
    formik.resetForm();
    setGridRecord([]);
    setDisplayColumns([]);
    formik.setFieldValue("reportDate", moment().startOf("day").format("YYYY-MM-DDTHH:mm"));
    formik.setFieldValue("type", "Received Containers");
  };

  return (
    <>
      <TabHeader module={moduleName} heading={pageHeading} />
      <Backdrop className={classes.backdrop} open={loader}>
        <CommonLoader />
      </Backdrop>

      <Box m={2} />
      <Card>
        <CardContent className={classes.cardMain}>
          <Grid container direction="row" justifyContent="space-between" alignItems="center">
            <Grid item md={9}>
              <Box mt={1} mb={2}>
                <Typography className={classes.pageHeading}> {pageHeading} </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3} direction="row">
              <Box textAlign="right" mt={1} mb={1}>
                <CommonButton cssClass="customSubmitBtn" onClick={() => resetSearch()}>
                  Reset
                </CommonButton>
                <CommonButton
                  cssClass="customSubmitBtn"
                  isGreen
                  onClick={() => formik.submitForm()}
                >
                  Search
                </CommonButton>
              </Box>
            </Grid>
          </Grid>

          <Grid spacing={1} container direction="row">
            <Grid item xs={12} md={3} lg={2}>
              <CommonTextFieldDate
                cssClassName="customTextField"
                label={"Date"}
                name={"reportDate"}
                datePickerRef={datePickerRef}
                value={formik.values.reportDate}
                onChange={(e: any) => {
                  formik.setFieldValue("reportDate", moment(e).format("YYYY-MM-DDTHH:mm"));
                }}
                error={formik.touched.reportDate && Boolean(formik.errors.reportDate)}
                helperText={formik.touched.reportDate && formik.errors.reportDate}
                maxDate={moment().format("YYYY-MM-DDTHH:mm")}
                required
              />
            </Grid>
            <Grid item xs={12} md={3} lg={2} style={{ paddingRight: 0 }}>
              <Box>
                <CommonAutoCompleteField
                  cssClassName="customTextField"
                  label="Type"
                  name="type"
                  optionsType={ContainerInOutReportType}
                  placeholder="Select Type"
                  disableClearable={true}
                  getOptionLabel={(option: any) => option || ""}
                  value={formik.values.type || null}
                  onChange={(option: any) => {
                    formik.setFieldValue("type", option || "");
                  }}
                  error={formik.touched.type && Boolean(formik.errors.type)}
                  helperText={formik.touched.type && formik.errors.type}
                />
              </Box>
            </Grid>
          </Grid>

          <CommonMaterialGrid
            rows={gridRecord}
            columns={displayColumns}
            defaultPerPageRecord={50}
            loader={loader}
            height={600}
            isExport={isExport}
            exportHandler={exportHandler}
            filename={`Container_In_Out_report_${Date.now()}`}
            isFooter={true}
          />
        </CardContent>
      </Card>
    </>
  );
}
